import React from 'react';
import { FiCheck, FiEdit, FiUserPlus, FiAlertCircle, FiClock, FiTrash2, FiRefreshCw } from 'react-icons/fi';

const TaskHistoryTimeline = ({ history }) => {
  const getActionIcon = (actionType) => {
    const icons = {
      created: FiCheck,
      assigned: FiUserPlus,
      reassigned: FiUserPlus,
      status_changed: FiEdit,
      priority_changed: FiAlertCircle,
      due_date_changed: FiClock,
      title_changed: FiEdit,
      description_changed: FiEdit,
      completed: FiCheck,
      deleted: FiTrash2,
      restored: FiRefreshCw
    };
    return icons[actionType] || FiEdit;
  };

  const getActionColor = (actionType) => {
    const colors = {
      created: 'bg-green-100 text-green-600',
      assigned: 'bg-blue-100 text-blue-600',
      reassigned: 'bg-blue-100 text-blue-600',
      status_changed: 'bg-yellow-100 text-yellow-600',
      priority_changed: 'bg-orange-100 text-orange-600',
      due_date_changed: 'bg-purple-100 text-purple-600',
      title_changed: 'bg-gray-100 text-gray-600',
      description_changed: 'bg-gray-100 text-gray-600',
      completed: 'bg-green-100 text-green-600',
      deleted: 'bg-red-100 text-red-600',
      restored: 'bg-blue-100 text-blue-600'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-600';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      created: 'Created',
      assigned: 'Assigned',
      reassigned: 'Reassigned',
      status_changed: 'Status Changed',
      priority_changed: 'Priority Changed',
      due_date_changed: 'Due Date Changed',
      title_changed: 'Title Changed',
      description_changed: 'Description Changed',
      completed: 'Completed',
      deleted: 'Deleted',
      restored: 'Restored'
    };
    return labels[actionType] || actionType;
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const d = new Date(datetime);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatValue = (value, actionType) => {
    if (!value) return '';

    // If it's a string, try to parse it as JSON
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === 'object' && parsed !== null) {
          value = parsed;
        }
      } catch (e) {
        // If parsing fails, return the string as is
        return value;
      }
    }

    // Format objects based on action type
    if (typeof value === 'object' && value !== null) {
      // For task creation, show a summary
      if (actionType === 'created') {
        const parts = [];
        if (value.title) parts.push(`Title: "${value.title}"`);
        if (value.priority) parts.push(`Priority: ${value.priority}`);
        if (value.category) parts.push(`Category: ${value.category}`);
        if (value.due_date) parts.push(`Due: ${new Date(value.due_date).toLocaleDateString()}`);
        return parts.join(', ');
      }

      // For assigned_to changes, try to show user info
      if (value.full_name || value.email) {
        return value.full_name || value.email;
      }

      // For dates
      if (value.due_date || value.completed_at) {
        const date = value.due_date || value.completed_at;
        return new Date(date).toLocaleString();
      }

      // For other objects, return a simple representation
      return Object.entries(value)
        .map(([key, val]) => `${key}: ${val}`)
        .join(', ');
    }

    return String(value);
  };

  if (!history || history.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        No history available
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {history.map((entry, index) => {
          const Icon = getActionIcon(entry.action_type);
          const colorClass = getActionColor(entry.action_type);
          const isLast = index === history.length - 1;

          return (
            <li key={entry.id}>
              <div className="relative pb-8">
                {!isLast && (
                  <span
                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex items-start space-x-3">
                  <div className="relative">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">
                          {entry.changed_by_name || 'System'}
                        </span>
                        <span className="text-gray-500"> {getActionLabel(entry.action_type)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {formatDateTime(entry.changed_at)}
                      </p>
                    </div>
                    {entry.notes && (
                      <div className="mt-2 text-sm text-gray-700">
                        <p>{entry.notes}</p>
                      </div>
                    )}
                    {(entry.old_value || entry.new_value) && (
                      <div className="mt-2 text-sm">
                        {entry.old_value && (
                          <div className="text-gray-500">
                            <span className="font-medium">From:</span>{' '}
                            <span className="line-through">{formatValue(entry.old_value, entry.action_type)}</span>
                          </div>
                        )}
                        {entry.new_value && (
                          <div className="text-gray-700">
                            <span className="font-medium">
                              {entry.action_type === 'created' ? 'Task Details:' : 'To:'}
                            </span>{' '}
                            <span className="font-medium text-green-600">{formatValue(entry.new_value, entry.action_type)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default TaskHistoryTimeline;
